'use strict';
const angular = require('angular');
const {copy, isNumber, isObject, isString, isUndefined, merge, toJson} = angular;

export function FirebaseQueueServiceProvider() {
  let pr_defaultSpec = {
    'start_state': null,
    'in_progress_state': 'in_progress',
    'finished_state': null,
    'error_state': 'error',
    'timeout': 300000, // 5 minutes
    'retries': 0 // don't retry
  };

  let pr_expires = 24 * 60 * 60 * 1000; // 1 day
  let pr_debugMode = false;

  this.defaultSpec = function (spec) {
    if (isObject(spec)) {
      pr_defaultSpec = spec;
    } else if (!isUndefined(spec)) {
      throw new Error("FirebaseQueueServiceProvider.defaultSpec expected an object and received '" + (typeof spec) + "'");
    } else {
      return pr_defaultSpec;
    }
  };

  this.cacheExpiration = function (expires) {
    if (isNumber(expires)) {
      pr_expires = expires;
    } else if (!isUndefined(expires)) {
      throw new Error("FirebaseQueueServiceProvider.defaultSpec expected a number and received '" + (typeof expires) + "'");
    } else {
      return pr_expires;
    }
  };

  this.debugMode = function (debugMode) {
    if (debugMode === true || debugMode === false) {
      pr_debugMode = debugMode;
    } else if (!isUndefined(debugMode)) {
      throw new Error("FirebaseQueueServiceProvider.debugMode expected a boolean and received '" + (typeof debugMode) + "'");
    } else {
      return pr_debugMode;
    }
  };

  this.$get = [
    '$log',
    '$q',
    '$timeout',
    'FirebaseUtil',
    '$exceptionHandler',
    'FirebaseConnectionService',
    function (
      $log,
      $q,
      $timeout,
      FirebaseUtil,
      $exceptionHandler,
      FirebaseConnectionService,
    ) {
      function FirebaseQueueService() {
        this.specsCache = {};
      }

      FirebaseQueueService.prototype.getSpec = getSpec;

      FirebaseQueueService.prototype.addTask = addTask;
      FirebaseQueueService.prototype.submitTask = submitTask;

      function getSpec(collection, specId) {
        var now = new Date().getTime();
        var specPath = collection + '/specs/' + specId;
        var spec = this.specsCache[specPath];

        if (specId === null) {
          return $q.resolve(pr_defaultSpec);
        } else if (spec && spec._cacheExpiration > pr_expires) {
          return $q.resolve(spec);
        } else {
          return FirebaseUtil.read(specPath).then((function (newSpec) {
            var spec;
            if (!isObject(newSpec)) {
              spec = copy(pr_defaultSpec);
            } else {
              spec = merge({}, pr_defaultSpec, newSpec);
            }

            spec._collection = collection;
            spec._cacheExpiration = now + pr_expires;

            this.specsCache[specPath] = spec;
            $log.debug('spec:', spec);
            return spec;
          }).bind(this));
        }
      }

      /**
       *
       * @param payload
       * @param userId
       * @param collection
       * @param specId
       * @param {Object} options
       * @param {number} options.timeout - The client side timeout for this request. The request must transition to !spec.start_state before this time is reached. Default 1 minute
       * @returns {promise}
       */
      function addTask(payload, userId, collection, specId, options) {
        var deferred = $q.defer(); // only this deferred can notify calling code so we must generate it here
        this.getSpec(collection, specId).then((function (spec) {
          return _addTaskInternal.call(this, payload, userId, spec, options, deferred);
        }).bind(this)).catch(function (error) {
          if (!isObject(error) || !isNumber(error.statusCode) || error.statusCode >= 500) {
            $exceptionHandler(error);
          }
          deferred.reject(error);
        });
        return deferred.promise;
      }

      /**
       *
       * @param payload
       * @param {string} [userId]
       * @param {Object} spec
       * @param {string} [spec._collection]
       * @param {string} [spec.start_state]
       * @param {string} spec.in_progress_state
       * @param {string} [spec.finished_state]
       * @param {string} [spec.error_state="error"]
       * @param {number} [spec.timeout=30000] - spec timeout. Default is 5 minutes.
       * @param {number} [spec.retries=0] - spec retries. 0 == don't retry
       * @param {Object} options
       * @param {number} options.timeout - The client side timeout for this request. The request must transition to !spec.start_state before this time is reached. Default 2 minutes
       * @param {Deferred} [deferred] - internal API
       * @returns {Promise}
       */
      function _addTaskInternal(payload, userId, spec, options, deferred) {
        if (!FirebaseConnectionService.online) {
          return $q.reject({message: 'No connection detected. Cannot send request to server'});
        }

        options = options || {timeout: 120 * 1000};
        deferred = deferred || $q.defer();
        var task = {_state: spec.start_state || null, user: userId || null, data: toJson(payload)};
        var completed = false;
        var errorState = spec.error_state || 'error';
        var timeout = options.timeout ? $timeout(onTimeout, options.timeout) : null;
        var taskRef = FirebaseUtil.firebaseRef(spec._collection + '/tasks').push();
        var latestSnapshot = {_state: spec.start_state};

        $log.debug('task:', task);

        deferred.promise.finally(fin);
        taskRef.set(task);
        taskRef.on('value', queueQuery, deferred.reject.bind(deferred));
        return deferred.promise;

        function queueQuery(ss) {
          var updatedTask = latestSnapshot = ss.val();

          if (!ss.exists()) {
            // default complete state. No data in task
            deferred.resolve(null);

          } else if (updatedTask._state === spec.start_state || isUndefined(updatedTask._state)) {
            // do nothing

          } else if (updatedTask._state === spec.in_progress_state) {
            if (isNumber(updatedTask._progress)) {
              deferred.notify({progress: updatedTask._progress});
            } else {
              deferred.notify({});
            }

          } else if (updatedTask._state === spec.finished_state) {
            deferred.resolve(__parseOutput(updatedTask));

          } else if (updatedTask._state === errorState) {
            deferred.reject(__parseError(updatedTask._error_details));
          }
        }

        function onTimeout() {
          timeout = null;
          if (!completed && (latestSnapshot._state === spec.start_state || isUndefined(latestSnapshot._state))) {
            deferred.reject({message: 'timed out after ' + (options.timeout / 1000) + ' seconds'});
            fin();
          }
        }

        function fin() {
          completed = true;
          taskRef.off('value', queueQuery);
          if (!pr_debugMode) taskRef.remove();
          if (timeout) $timeout.cancel(timeout);
        }
      }

      /**
       *
       * @param payload
       * @param userId
       * @param collection
       * @param specId
       * @returns {promise}
       */
      function submitTask(payload, userId, collection, specId) {
        return this.getSpec(collection, specId).then((function (spec) {
          return _submitTaskInternal.call(this, payload, userId, spec);
        }).bind(this)).catch(function (error) {
          $exceptionHandler(error);
          return $q.reject(error);
        });
      }

      /**
       *
       * @param payload
       * @param {string} [userId]
       * @param {Object} spec
       * @param {string} [spec._collection]
       * @param {string} [spec.start_state]
       * @param {string} spec.in_progress_state
       * @param {string} [spec.finished_state]
       * @param {string} [spec.error_state="error"]
       * @param {number} [spec.timeout=30000] - spec timeout. Default is 5 minutes.
       * @param {number} [spec.retries=0] - spec retries. 0 == don't retry
       * @returns {Promise}
       */
      function _submitTaskInternal(payload, userId, spec) {
        if (!FirebaseConnectionService.online) {
          return $q.reject({message: 'No connection detected. Cannot send request to server'});
        }

        var task = {_state: spec.start_state || null, user: userId || null, data: toJson(payload)};
        var taskRef = FirebaseUtil.firebaseRef(spec._collection + '/tasks').push();

        $log.debug('task:', task);

        return taskRef.set(task);
      }

      function __parseOutput(task) {
        if (task.data && isString(task.data)) {
          try {
            return JSON.parse(task.data)
          } catch (any) {
            return task.data
          }
        } else if (task) {
          return task.data
        } else {
          return null
        }
      }

      function __parseError(response) {
        if (response.error && isString(response.error)) {
          try {
            return JSON.parse(response.error)
          } catch (any) {
            return response.error
          }
        } else {
          return response.error
        }
      }

      return new FirebaseQueueService();
    }];
}



// WEBPACK FOOTER //
// ./src/js/_libs/firebase-mq/FirebaseQueueService.js